import { Tooltip, Button, Icon } from '@zoom/components';
import classnames from 'classnames';
import { AnyObject } from 'final-form';
import React, { useState, useContext, useRef } from 'react';
import { Form, FormProps, FormRenderProps } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import PerfectScrollbar from 'react-perfect-scrollbar';

import { SIDEBAR_CLOSE_BUTTON, SIDEBAR_TOGGLE_BUTTON } from './constants';

export const SIDEBAR_UPDATE_TIMEOUT = 300;

export const defaultSidebarValues = {
  isSidebarExpanded: false,
  isSidebarOpen: false,
  isSidebarPinned: false,
  toggleSidebarExpanded: () => null,
  expandSidebar: () => null,
  shrinkSidebar: () => null,
  toggleSidebar: () => null,
  openSidebar: () => null,
  closeSidebar: () => null,
  pinSidebar: () => null,
  unpinSidebar: () => null,
  resetSidebar: () => null,
  scrollbarContainerRef: null,
  scrollbarRef: null,
  setScrollbarContainerRef: () => null,
  handleScrollbarUpdate: () => null,
  handleScrollPosition: () => null,
};

export const SidebarContext = React.createContext<{
  isSidebarExpanded: boolean;
  isSidebarOpen: boolean;
  isSidebarPinned: boolean;
  toggleSidebarExpanded: () => void;
  expandSidebar: () => void;
  shrinkSidebar: () => void;
  toggleSidebar: () => void;
  openSidebar: () => void;
  closeSidebar: () => void;
  pinSidebar: () => void;
  unpinSidebar: () => void;
  resetSidebar: () => void;
  scrollbarRef: React.RefObject<PerfectScrollbar> | null;
  scrollbarContainerRef: HTMLElement | null;
  setScrollbarContainerRef: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
  handleScrollbarUpdate: () => void;
  handleScrollPosition: (position?: number) => void;
}>(defaultSidebarValues);

export const SidebarProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [isSidebarExpanded, setSidebarExpanded] = useState(false);
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [isSidebarPinned, setSidebarPinned] = useState(false);
  const [scrollbarContainerRef, setScrollbarContainerRef] = useState<HTMLElement | null>(null);
  const scrollbarRef = useRef<PerfectScrollbar>(null);

  const openSidebar = () => setSidebarOpen(true);

  const closeSidebar = () => setSidebarOpen(false);

  const toggleSidebar = () => setSidebarOpen((open) => !open);

  const expandSidebar = () => {
    if (!isSidebarPinned) {
      setSidebarExpanded(true);
    }
  };

  const shrinkSidebar = () => setSidebarExpanded(false);

  const toggleSidebarExpanded = () => {
    if (!isSidebarPinned) {
      setSidebarExpanded(!isSidebarExpanded);
    }
  };

  const pinSidebar = () => setSidebarPinned(true);

  const unpinSidebar = () => setSidebarPinned(false);

  const resetSidebar = () => {
    closeSidebar();
    shrinkSidebar();
    unpinSidebar();
  };

  const handleScrollbarUpdate = () => {
    setTimeout(() => {
      if (scrollbarRef && scrollbarRef.current && scrollbarContainerRef) {
        scrollbarRef.current.forceUpdate();
      }
    }, SIDEBAR_UPDATE_TIMEOUT);
  };

  const handleScrollPosition = (position = 0) => {
    if (scrollbarContainerRef) {
      scrollbarContainerRef.setAttribute('style', 'scroll-behavior: smooth;');
      scrollbarContainerRef.scrollTop = position;
      scrollbarContainerRef.removeAttribute('style');
      scrollbarContainerRef.focus();
    }
  };

  return (
    <SidebarContext.Provider
      value={{
        isSidebarExpanded,
        isSidebarOpen,
        isSidebarPinned,
        toggleSidebarExpanded,
        expandSidebar,
        shrinkSidebar,
        toggleSidebar,
        openSidebar,
        pinSidebar,
        unpinSidebar,
        resetSidebar,
        closeSidebar,
        scrollbarContainerRef,
        scrollbarRef,
        setScrollbarContainerRef,
        handleScrollbarUpdate,
        handleScrollPosition,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
};

export const SidebarConsumer = SidebarContext.Consumer;

export const useSidebar = () => useContext(SidebarContext);

export const sidebarClassName = 'sidebar-container';
export const expandedClassName = `${sidebarClassName}--expanded`;

export const SidebarContainer: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { isSidebarExpanded } = useSidebar();

  return <aside className={classnames(sidebarClassName, { [expandedClassName]: isSidebarExpanded })}>{children}</aside>;
};

// TODO: generic type checking
interface SidebarProps extends React.PropsWithChildren {
  tag?: React.ElementType;
  [name: string]: any;
}

export const Sidebar: React.FC<SidebarProps> = ({ children, className, tag: Tag = 'div', ...props }) => (
  <Tag {...props} className={classnames('sidebar', className)}>
    {children}
  </Tag>
);

interface SidebarHeaderProps extends React.PropsWithChildren {
  onClose?: () => void;
  showExpandButton?: boolean;
}

export const SidebarHeader: React.FC<SidebarHeaderProps> = ({ children, showExpandButton = false, onClose }) => {
  const { closeSidebar, toggleSidebarExpanded, isSidebarExpanded } = useSidebar();
  const { t } = useTranslation();
  const togglerHolder = useRef<HTMLDivElement>(null);

  const handleClose = () => {
    if (onClose) {
      onClose();
    }

    closeSidebar();
  };

  const handleToggle = () => {
    toggleSidebarExpanded();
    togglerHolder.current?.querySelector('button')?.blur(); // Reset toggled button styling
  };

  return (
    <div className="sidebar__header">
      <div ref={togglerHolder}>
        {showExpandButton && (
          <Button
            kind="icon"
            onClick={handleToggle}
            className="sidebar__header-toggler"
            data-test-id={SIDEBAR_TOGGLE_BUTTON}
          >
            <Icon color="#239ed8" kind={isSidebarExpanded ? 'chevron-right' : 'chevron-left'} size={12} />
          </Button>
        )}
      </div>
      <>{children}</>
      <Tooltip message={t('layout.action.close')} placement="bottom">
        <Button
          kind="icon"
          onClick={handleClose}
          aria-label={t('layout.action.close')}
          data-test-id={SIDEBAR_CLOSE_BUTTON}
        >
          <Icon color="#239ed8" kind="cross" size={12} />
        </Button>
      </Tooltip>
    </div>
  );
};

export const SidebarBody: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { scrollbarRef, setScrollbarContainerRef } = useSidebar();

  return (
    <PerfectScrollbar
      className="sidebar__body"
      containerRef={setScrollbarContainerRef}
      options={{ suppressScrollX: true }}
      ref={scrollbarRef}
    >
      {children}
    </PerfectScrollbar>
  );
};

type SidebarFormWrapper<T> = {
  children: (formProps: FormRenderProps<T>) => React.ReactNode;
  className?: string;
  name?: string;
} & FormProps<T>;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const SidebarFormWrapper = <T extends AnyObject>({
  children,
  className,
  name,
  ...props
}: SidebarFormWrapper<T>): React.ReactElement<SidebarFormWrapper<T>> => (
  <Form<T> {...props}>
    {(formProps) => (
      <Sidebar tag="form" onSubmit={formProps.handleSubmit} className={className} name={name}>
        {children(formProps)}
      </Sidebar>
    )}
  </Form>
);
